import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "./LoadingScreen";
import { useOrganization } from "../context/OrganizationContext";
import { useProject } from "../context/ProjectContext";
import apiService from "../services/apiService";
import ApiKeyManager from "./ApiKeyManager";
import { DOCS_URL } from "../constants/branding";

const API_KEY_MANAGER_ROOT_ID = "dashboard-api-key-manager";

const Dashboard = () => {
  const navigate = useNavigate();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const { selectedProject, loading: projectLoading } = useProject();
  const [apiKeysCount, setApiKeysCount] = useState(0);
  const apiKeyManagerRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    if (!token || !email) {
      navigate("/login");
      return;
    }

    // Welcome toast on first dashboard load post-signup. Set by either
    // GoogleAuthCallback (when ?is_new=1 from backend) or Verify.jsx (after
    // the user clicks the email-verification link). Cleared immediately
    // so subsequent dashboard mounts don't re-fire it.
    if (localStorage.getItem("welcomeNewUser") === "1") {
      const name = localStorage.getItem("welcomeNewUserName") || "";
      localStorage.removeItem("welcomeNewUser");
      localStorage.removeItem("welcomeNewUserName");
      const greeting = name
        ? `Welcome to DataNodes, ${name}!`
        : "Welcome to DataNodes!";
      toast.success(`${greeting} 🎉 Your workspace is ready.`, {
        duration: 7000,
        id: "welcome-new-user",
      });
    }
  }, [navigate]);

  useEffect(() => {
    if (!currentOrganization?.id || !selectedProject?.id) {
      setApiKeysCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const keys = await apiService.getApiKeys(
          currentOrganization.id,
          selectedProject.uuid,
        );
        if (!cancelled) setApiKeysCount(Array.isArray(keys) ? keys.length : 0);
      } catch {
        if (!cancelled) setApiKeysCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.id, selectedProject?.id]);

  if (projectLoading || orgLoading) {
    return <LoadingScreen />;
  }

  const badgeClass =
    apiKeysCount >= 3
      ? "border border-red-500/35 bg-red-500/10 text-red-300"
      : "bg-[#1a2b3c] px-2.5 py-0.5 text-xs font-semibold text-[#49B7F8] sm:px-3 sm:py-1 sm:text-sm";

  return (
    <div>
      <div className="min-h-screen bg-[#0d0d0d]">
        <header className="sticky top-0 z-10 border-b border-white/[0.06] bg-[#0d0d0d]">
          <div className="px-4 py-4 sm:px-8 sm:py-5">
            {/* Row 1: title + badge | Create + help */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                <h1 className="font-title text-xl font-bold tracking-tight text-white sm:text-2xl">
                  API Keys
                </h1>
                <span
                  className={`font-secondary rounded-sm ${badgeClass}`}
                  aria-live="polite"
                >
                  {selectedProject ? `${apiKeysCount}/3` : "—/3"}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  data-create-api-key-trigger
                  onClick={() => apiKeyManagerRef.current?.openCreatePanel()}
                  disabled={!selectedProject}
                  className={`font-secondary rounded-sm px-4 py-2.5 text-sm font-bold transition-colors sm:text-[15px] ${
                    !selectedProject || apiKeysCount >= 3
                      ? "cursor-not-allowed bg-white/10 text-white/40"
                      : "bg-primary text-[#0a0a0a] hover:bg-primary-hover"
                  }`}
                >
                  Create API Key
                </button>
                <a
                  href={DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open documentation"
                  className="flex h-9 w-9 shrink-0 items-center justify-center transition-colors hover:bg-white/[0.06] hover:text-white"
                >
                  <i className="fa-solid fa-question-circle text-gray-400 text-md"></i>
                </a>
              </div>
            </div>{" "}
          </div>
        </header>

        {/* Flow slot: Create API Key bar portals here so it pushes <main> down (not position:fixed) */}
        <div
          id="dashboard-api-key-create-mount"
          className="w-full"
          aria-live="polite"
        />

        <main className="mx-auto max-w-screen-2xl px-4 sm:px-8 pt-3">
          <p className="font-secondary mb-2 text-sm text-[#888888]">
            Manage your high-performance RPC endpoints.
          </p>

          {selectedProject ? (
            <div id={API_KEY_MANAGER_ROOT_ID}>
              <ApiKeyManager
                ref={apiKeyManagerRef}
                project={selectedProject}
                embedded
                onApiKeysCountChange={setApiKeysCount}
              />
            </div>
          ) : (
            <div className="rounded-sm border border-white/10 bg-[#131313] p-6 text-center sm:p-12">
              <svg
                className="mx-auto mb-4 h-12 w-12 text-gray-600 sm:h-16 sm:w-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
              <h3 className="mb-2 text-lg font-medium text-white sm:text-xl">
                No Project Selected
              </h3>
              <p className="text-sm text-gray-400">
                Please select a project to view and manage API keys.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
